import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import { autoCreditApi } from '@/integrations/app.api/auto-credit.api';
import type { IRequestCreateUnverifiedDraftDto } from '@/types/newOrder';

import type { TOrderStepStore } from '../types/store';
import { createAsyncThunk } from './create-async-thunk';
import { changeRequestStatus, getRequestById } from './newOrder.slice';

export const requestCreateUnverifiedDraft = createAsyncThunk(
  'orderStep/requestCreateUnverifiedDraft',
  async (data: IRequestCreateUnverifiedDraftDto, { dispatch }) => {
    try {
      const response = await autoCreditApi.requestCreateUnverifiedDraft(data);
      await dispatch(
        getRequestById({
          reqId: response?.request_uuid || ''
        })
      );
      return response?.request_uuid || '';
    } catch (error) {
      throw new Error(String(error), { cause: error });
    }
  }
);

export const validateOTP = createAsyncThunk(
  'orderStep/validateOTP',
  async (data: { uuid: string; otp: string }, { dispatch }) => {
    const { ...body } = data;
    const responseData = await autoCreditApi.validateOTP(body);
    if (responseData.result) {
      await dispatch(
        changeRequestStatus({ reqId: data.uuid, newStatus: 'draft' })
      );
      await dispatch(getRequestById({ reqId: data.uuid }));
    }
    return responseData;
  }
);

export const retryValidateOTP = createAsyncThunk(
  'orderStep/retryOTP',
  async (
    data: { uuid: string; clientPhone: string; type: 'WhatsApp' | 'SMS' },
    { dispatch }
  ) => {
    try {
      const responseData = await autoCreditApi.retryOTP(data);
      if (responseData.result) {
        await dispatch(
          changeRequestStatus({ reqId: data.uuid, newStatus: 'draft' })
        );
      }
      return responseData;
    } catch (error) {
      throw new Error(String(error), { cause: error });
    }
  }
);

const initialState: TOrderStepStore = {
  otpCode: '',
  isLoading: false,
  additionalInfo: {
    files: null
  },
  personalInfo: {
    jobAddress: '',
    sex: '',
    docType: '',
    salary: '',
    addContactsIsRelated: '',
    addContactsTelNum: '',
    children: '',
    additionalSalary: '',
    companyName: '',
    jobTitle: '',
    socialStatus: '',
    workTelNumber: '',
    addContactsFullName: '',
    companyType: '',
    familyStatus: '',
    dateBirth: null,
    spouseInfo: {
      spouseIin: '',
      spouseFullName: '',
      spousePlaceBirth: '',
      spouseTelNum: '',
      spouseDateBirth: null,
      spouseCompanyName: '',
      spouseSalary: '',
      spouseAddSalary: '',
      isMarried: false
    }
  }
};

export const orderStepSlice = createSlice({
  name: 'orderStepSlice',
  initialState,
  reducers: {
    onSetOrderOtpCode(state, action: PayloadAction<string>) {
      state.otpCode = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestCreateUnverifiedDraft.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(requestCreateUnverifiedDraft.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(requestCreateUnverifiedDraft.rejected, (state) => {
        state.isLoading = false;
      });
  }
});

export const { onSetOrderOtpCode } = orderStepSlice.actions;
